import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, X, FileText, Loader2, Plus } from "lucide-react";
import { labelClass } from "./fieldStyles";
import type { DocumentRef } from "../../types/form";
import { useFormStore } from "../../store/formStore";
import { getApi } from "../../api";
import { saveCurrentDraft } from "../../services/saveDraft";
import { trackWarning } from "../../telemetry/appInsights";
import { ACCEPTED_TYPES, ACCEPTED_EXTENSIONS, MAX_SIZE_MB, MAX_SIZE_BYTES, formatFileSize } from "./fileUploadConstants";

interface MultiFileUploadProps {
  docType: string;
  label: string;
  files: DocumentRef[];
  onAdd: (ref: DocumentRef) => void;
  onRemove: (index: number) => void;
  hint?: string;
}

/**
 * Multi-file upload with drag-and-drop. Renders a list of uploaded files
 * with individual delete buttons, plus an "Add another document" zone.
 */
export function MultiFileUpload({
  docType,
  label,
  files,
  onAdd,
  onRemove,
  hint,
}: MultiFileUploadProps) {
  const formId = useFormStore((s) => s.formId);
  const [dragOver, setDragOver] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [slowUpload, setSlowUpload] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => { if (slowTimerRef.current) clearTimeout(slowTimerRef.current); }, []);

  const uploading = uploadCount > 0;
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  const handleFiles = useCallback(async (fileList: FileList) => {
    if (!formId) {
      setUploadError("Form not loaded");
      return;
    }
    const allFiles = Array.from(fileList);
    const filesToUpload = allFiles.filter(f => f.size > 0);
    if (filesToUpload.length === 0) {
      if (filesToUpload.length < allFiles.length) setUploadError("Empty files were skipped");
      return;
    }

    setUploadError(filesToUpload.length < allFiles.length ? "Empty files were skipped" : null);
    // Persist the form (incl. the field that makes this upload control visible)
    // before any file is written to Salesforce — see FileUpload for the rationale.
    if (!(await saveCurrentDraft())) {
      setUploadError("Couldn't save the form before uploading. Please try again.");
      return;
    }
    setSlowUpload(false);
    const batchSize = filesToUpload.reduce((sum, f) => sum + f.size, 0);
    if (batchSize > 5 * 1024 * 1024)
      slowTimerRef.current = setTimeout(() => setSlowUpload(true), 20_000);
    const api = getApi();
    let runningTotal = files.reduce((sum, f) => sum + f.size, 0);
    for (const file of filesToUpload) {
      if (runningTotal + file.size > MAX_SIZE_BYTES) {
        setUploadError(`Total uploads cannot exceed ${MAX_SIZE_MB}MB`);
        break;
      }
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        setUploadError(`Accepted file types: ${ACCEPTED_EXTENSIONS.map(e => e.toUpperCase()).join(", ")}`);
        continue;
      }
      setUploadCount((c) => c + 1);
      try {
        const result = await api.uploadFile(formId, docType, file);
        onAdd(result);
        runningTotal += file.size;
      } catch {
        setUploadError(`Failed to upload ${file.name}. Please try again.`);
      } finally {
        setUploadCount((c) => c - 1);
      }
    }
    clearTimeout(slowTimerRef.current);
    setSlowUpload(false);
  }, [formId, docType, onAdd, files]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (uploadCount > 0) return;
    if (e.dataTransfer.files.length > 0) void handleFiles(e.dataTransfer.files);
  }, [handleFiles, uploadCount]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) void handleFiles(e.target.files);
    if (inputRef.current) inputRef.current.value = "";
  }, [handleFiles]);

  const handleRemove = useCallback(async (index: number) => {
    const file = files[index];
    setDeletingIndex(index);
    if (file) {
      try {
        const api = getApi();
        await api.deleteFile(file.fileId);
        onRemove(index);
      } catch {
        trackWarning("MultiFileUpload: delete failed", { fileId: file.fileId, docType });
        setUploadError("Failed to remove file. Please try again.");
      }
    } else {
      onRemove(index);
    }
    setDeletingIndex(null);
  }, [files, onRemove, docType]);

  return (
    <div className="mt-3">
      <span className={labelClass()}>{label}</span>
      {hint && <p className="text-xs text-gray-500 mt-0.5 mb-2">{hint}</p>}

      {files.length > 0 && (
        <ul className="space-y-2 mb-3 list-none p-0 m-0">
          {files.map((file, i) => {
            const isDeleting = deletingIndex === i;
            return (
              <li key={file.fileId} className={`flex items-center gap-3 border rounded-lg px-4 py-3 transition-opacity ${isDeleting ? "bg-gray-100 border-gray-200 opacity-50" : "bg-gray-50 border-gray-200"}`}>
                {isDeleting ? (
                  <Loader2 size={20} className="text-gray-400 shrink-0 animate-spin" aria-hidden="true" />
                ) : (
                  <FileText size={20} className="text-gray-400 shrink-0" aria-hidden="true" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">{isDeleting ? "Removing..." : `${formatFileSize(file.size)}`}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void handleRemove(i)}
                  disabled={isDeleting || deletingIndex !== null}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-danger transition-colors cursor-pointer shrink-0 disabled:opacity-30 disabled:cursor-default"
                  aria-label={`Remove ${file.name}`}
                >
                  <X size={18} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <label
        aria-label={`Upload ${label}`}
        className={`flex flex-col items-center gap-2 border-2 border-dashed rounded-lg px-4 py-6 cursor-pointer transition-all active:scale-[0.98] focus-within:ring-2 focus-within:ring-brand-green-50 focus-within:ring-offset-2 ${
          dragOver
            ? "border-brand-green bg-brand-green-05"
            : "border-gray-300 hover:border-brand-green hover:bg-brand-green-05"
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
      >
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          accept={ACCEPTED_TYPES}
          multiple
          onChange={handleInputChange}
          disabled={uploading}
        />
        {uploading ? (
          <Loader2 size={24} className="text-brand-green animate-spin" aria-hidden="true" />
        ) : files.length > 0 ? (
          <Plus size={24} className="text-gray-400" aria-hidden="true" />
        ) : (
          <Upload size={24} className="text-gray-400" aria-hidden="true" />
        )}
        <span className="text-sm font-medium text-gray-600" aria-live="polite">
          {uploading
            ? slowUpload
              ? "This may take a few minutes depending on file sizes and number of files. Please wait..."
              : "Uploading..."
            : files.length > 0 ? "Add another document" : "Tap to upload or drag & drop"}
        </span>
        <span className="text-xs text-gray-500">
          PDF, JPG, PNG, XLSX, CSV — Max {MAX_SIZE_MB}MB total
          {totalSize > 0 && ` (${formatFileSize(totalSize)} used)`}
        </span>
      </label>
      {uploadError && <p role="alert" className="text-xs text-danger mt-1">{uploadError}</p>}
    </div>
  );
}
